/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard (C) Copyright IBM Corp. 2020, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'jquery',
	'react',
	'prop-types',
	'@ba/ba-ui-carbon-toolkit',
	'../DataSourcePanelIconHelper',
	'dashboard-analytics/lib/@waca/core-client/js/core-client/utils/JQueryTabbable'
], function (_, $, React, PropTypes, Toolkit, DataSourcePanelIconHelper) {

	const {
		Breadcrumb,
		Button,
		FlexItem,
		FlexLayout
	} = Toolkit;

	class DataSourcePanelPage extends React.Component {
		static propTypes = {
			dashboardApi: PropTypes.func.isRequired,
			previousPages: PropTypes.arrayOf(
				PropTypes.shape({
					id: PropTypes.string.isRequired,
					title: PropTypes.string.isRequired,
					onSelect: PropTypes.func.isRequired
				})
			)
		}

		constructor(props) {
			super(props);

			this.state = {
				buttons: []
			};

			this._rootNode = React.createRef();

			this._iconHelper = new DataSourcePanelIconHelper({
				iconsFeature: this.props.dashboardApi.getFeature('Icons')
			});
			this._buttonsPromise = this._setupButtonsState();
		}

		_setupButtonsState() {
			const rawButtonsDefinition = this._loadButtonsDefinition();
			if(rawButtonsDefinition) {
				return rawButtonsDefinition.then(
					rawButtonsCollection => {
						if (rawButtonsCollection) {
							return this._buildButtonsDefinition(rawButtonsCollection).then(
								buttons => this.setState({ buttons })
							);
						}
					}
				);
			}
		}

		resetFocus() {
			if(this._rootNode.current) {
				const focusableNodes = $(this._rootNode.current).find(':focusable');
				if(focusableNodes[0]) {
					focusableNodes[0].focus();
					return true;
				}
			}
		}

		getButtonCollectionId() {}

		getButtonConstructorPayload() {
			return {
				dashboardApi: this.props.dashboardApi
			};
		}

		_loadButtonsDefinition() {
			const buttonCollectionId = this.getButtonCollectionId();
			return buttonCollectionId && this.props.dashboardApi.findGlassCollection(buttonCollectionId);
		}

		_buildButtonsDefinition(rawButtonsCollection) {
			// Make a copy of collection. Assume each entry is a shallow object.
			const buttonsCollection = rawButtonsCollection.map(
				buttonDefinition => ({ ...buttonDefinition })
			);
			buttonsCollection.sort(function (a, b) {
				return (a.weight || 0) - (b.weight || 0);
			});
			return Promise.all(
				buttonsCollection.map(
					buttonDefinition => (
						this._loadButtonActionControllers(buttonDefinition)
					).then(actionController => {
						buttonDefinition.actionController = actionController;
						return buttonDefinition;
					})
				)
			);
		}

		_loadButtonActionControllers(buttonDefinition) {
			return new Promise((resolve) =>
				require(
					[buttonDefinition.actionController],
					ActionController =>
						resolve(new ActionController(this.getButtonConstructorPayload()))
				)
			);
		}

		render() {
			return (
				<FlexLayout
					className='dataSourcePanelStructure'
					direction='column'
				>
					<FlexItem
						className='dataSourcePanelHeaderContainer'
						grow={false}
						shrink={false}
					>
						{this.renderHeader()}
					</FlexItem>
					<FlexItem
						className='dataSourcePanelContents'
						grow={true}
						shrink={true}
					>
						{this.renderPanel()}
					</FlexItem>
				</FlexLayout>
			);
		}

		renderHeader() {
			return (
				<div
					className='dataSourcePanelHeader'
					ref={this._rootNode}
				>
					<FlexLayout
						direction='row'
						alignItems='flex-end'
					>
						<FlexItem
							grow={true}
							shrink={true}
						>
							{this.renderTitle()}
						</FlexItem>
						{
							this.renderButtons().map(
								(button, i) => (
									<FlexItem
										key={i}
										grow={false}
										shrink={false}
									>
										{button}
									</FlexItem>
								)
							)
						}
					</FlexLayout>
				</div>
			);
		}

		renderTitle() {
			const ancestors = this.props.previousPages || [];

			const crumbs = [
				...ancestors, this.getTitleParams()
			].map(
				({ id, title }) => ({
					id,
					defaultName: title
				})
			);
			return (
				<Breadcrumb
					className='dataSourcePanelTitle'
					ancestors={crumbs}
					noTrailingSlash={true}
					navCallback={
						index => {
							if (index < ancestors.length) {
								ancestors[index].onSelect();
							}
						}
					}
					hideBackButton={true}
					historyCallback={
						() => {
							if (ancestors.length) {
								ancestors[0].onSelect();
							}
						}
					}
				/>
			);
		}

		getTitleParams() {}

		renderButtons() {
			return this.state.buttons && this.state.buttons.map(
				(buttonDefinition) =>
					<div
						className='dataSourcePanelButton'	//Button overrides padding - need div to wrap
						key={buttonDefinition.name}
					>
						<Button
							iconSize='small'
							variant='icon'
							icon={this._iconHelper.getActionIconId(buttonDefinition.icon)}
							aria-label={buttonDefinition.label}
							title={buttonDefinition.label}
							tooltipAlignment='start'
							tooltipPosition='left'
							onClick={this._getButtonOnClickHandler(buttonDefinition)}
						/>
					</div>
			);
		}

		_getButtonOnClickHandler(buttonDefinition) {
			if (buttonDefinition.actionController) {
				return evt => {
					evt.persist();	//Otherwise React SyntheticEvent gets garbage collected before it can be used
					buttonDefinition.actionController.execute(evt, buttonDefinition);
				};
			}
		}

		renderPanel() {}
	}

	return DataSourcePanelPage;
});
